import { useState } from "react";
import { CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/marketing/PageShell";
import { useSiteContent } from "@/hooks/useSiteContent";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const TOPICS = ["General", "Sales", "Support", "Billing", "Press"];
const EMPTY = { full_name: "", email: "", company: "", topic: "General", message: "", website: "" };

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">
      {label} {required && <span className="text-oath-accent">*</span>}
    </Label>
    {children}
  </div>
);

const InfoRow = ({ icon: Icon, label, children, testid }) => (
  <div className="flex gap-3" data-testid={testid}>
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-white"><Icon className="h-4 w-4" /></span>
    <div>
      <div className="font-mono-label text-[9px] text-white/50">{label}</div>
      <div className="mt-0.5 break-words text-sm text-white">{children}</div>
    </div>
  </div>
);

const ContactInfo = () => {
  const { contact } = useSiteContent();
  return (
    <aside className="overflow-hidden rounded-3xl bg-oath-navy text-white lg:self-start" data-testid="contact-info">
      <img src="/img/people/support.jpg" alt="" className="aspect-[16/10] w-full object-cover" data-testid="contact-support-image" />
      <div className="p-6 sm:p-8">
      <h2 className="font-display text-xl font-bold">Reach us directly</h2>
      <p className="mt-2 text-sm text-white/60">We reply within one business day.</p>
      <div className="mt-6 grid gap-5">
        <InfoRow icon={Mail} label="EMAIL" testid="contact-info-email">
          <a href={`mailto:${contact.support_email}`} className="hover:underline">{contact.support_email}</a>
        </InfoRow>
        {contact.phone && <InfoRow icon={Phone} label="PHONE" testid="contact-info-phone"><a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a></InfoRow>}
        {contact.address && <InfoRow icon={MapPin} label="ADDRESS" testid="contact-info-address">{contact.address}</InfoRow>}
      </div>
      </div>
    </aside>
  );
};

const Success = ({ onReset }) => (
  <div className="py-10 text-center" data-testid="contact-success">
    <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></span>
    <h2 className="mt-4 font-display text-2xl font-bold text-oath-ink">Message sent</h2>
    <p className="mt-2 text-sm text-oath-secondary">Thanks for reaching out. We'll get back to you at the email you provided.</p>
    <button onClick={onReset} className="mt-5 text-sm text-oath-accent underline" data-testid="contact-send-another">Send another message</button>
  </div>
);

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell eyebrow="— CONTACT —" title="Contact us">
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-3xl border border-gray-200 bg-white p-6 sm:p-8">
          {sent ? <Success onReset={() => setSent(false)} /> : (
            <form onSubmit={submit} data-testid="contact-form">
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
              <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">Topic <span className="text-oath-accent">*</span></Label>
              <div className="mt-3 flex flex-wrap gap-2">
                {TOPICS.map((t) => (
                  <button type="button" key={t} onClick={() => setForm((f) => ({ ...f, topic: t }))} data-testid={`contact-topic-${t.toLowerCase()}`}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${form.topic === t ? "border-oath-ink bg-oath-ink text-white" : "border-gray-200 text-oath-ink hover:border-gray-400"}`}>{t}</button>
                ))}
              </div>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Field label="Full name" required><Input data-testid="contact-field-name" value={form.full_name} onChange={set("full_name")} required minLength={2} /></Field>
                <Field label="Email" required><Input data-testid="contact-field-email" type="email" value={form.email} onChange={set("email")} required /></Field>
              </div>
              <div className="mt-5"><Field label="Company"><Input data-testid="contact-field-company" value={form.company} onChange={set("company")} /></Field></div>
              <div className="mt-5">
                <Field label="Message" required>
                  <Textarea data-testid="contact-field-message" rows={5} value={form.message} onChange={set("message")} required minLength={10} placeholder="How can we help?" />
                </Field>
              </div>
              <p className="mt-4 text-xs text-oath-muted">Please don't include sensitive details such as dates of birth or account numbers.</p>
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "SEND MESSAGE"}
              </button>
            </form>
          )}
        </div>
        <ContactInfo />
      </div>
    </PageShell>
  );
}
